import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useCurrentCompany } from "../components/CompanySwitcher";

interface Template { id: number; company_id: number; language: "en" | "th"; subject: string; body: string; updated_at: string }

const PLACEHOLDERS = [
  ["{{client_name}}",                 "Client/company name"],
  ["{{contact_name}}",                "Contact person (may be empty)"],
  ["{{contact_name_or_client_name}}", "Contact, falling back to client name"],
  ["{{company_name}}",                "Your company (sender)"],
  ["{{estimates_table}}",             "HTML table of open invoices"],
  ["{{total_amount}}",                "Total outstanding"],
  ["{{estimate_count}}",              "Number of open invoices"],
  ["{{currency}}",                    "Currency code (e.g. THB)"],
];

export default function Templates() {
  const { current } = useCurrentCompany();
  const [en, setEn] = useState<Template | null>(null);
  const [th, setTh] = useState<Template | null>(null);
  const [active, setActive] = useState<"en" | "th">("en");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  async function refresh() {
    if (!current) return;
    const r = await api<{ en: Template | null; th: Template | null }>(`/api/templates?company_id=${current.id}`);
    setEn(r.en); setTh(r.th);
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [current?.id]);

  const t = active === "en" ? en : th;
  function setT(patch: Partial<Template>) {
    const next = { ...(t as Template), ...patch };
    if (active === "en") setEn(next); else setTh(next);
  }

  async function save() {
    if (!current || !t) return;
    setSaving(true);
    try {
      await api("/api/templates", {
        method: "PATCH",
        body: JSON.stringify({
          company_id: current.id,
          language: active,
          subject: t.subject,
          body: t.body,
        }),
      });
      setSavedAt(new Date().toLocaleTimeString());
    } finally { setSaving(false); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="flex rounded-md border border-slate-200 bg-white p-0.5">
          {(["en", "th"] as const).map((l) => (
            <button key={l}
                    className={`px-3 py-1.5 text-sm rounded ${active === l ? "bg-indigo-50 text-indigo-700" : "text-slate-600"}`}
                    onClick={() => setActive(l)}>
              {l === "en" ? "English template" : "Thai template (ไทย)"}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
          {savedAt && <span>Saved {savedAt}</span>}
          <button className="btn-primary" onClick={save} disabled={!t || saving}>
            {saving ? "Saving…" : "Save template"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="card space-y-3 p-4 lg:col-span-2">
          {!t && <div className="text-sm text-slate-500">No template yet — type one and save.</div>}
          {t && (
            <>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Subject</label>
                <input className="w-full" value={t.subject} onChange={(e) => setT({ subject: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Body (HTML)</label>
                <textarea className="h-72 w-full font-mono text-xs" value={t.body}
                          onChange={(e) => setT({ body: e.target.value })} />
              </div>
            </>
          )}
        </div>

        <div className="card p-4 text-xs">
          <div className="font-semibold">Placeholders</div>
          <div className="mt-2 space-y-1">
            {PLACEHOLDERS.map(([k, d]) => (
              <div key={k} className="flex items-baseline gap-2">
                <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono">{k}</code>
                <span className="text-slate-500">{d}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded bg-amber-50 p-2 text-amber-800">
            Tip: <code>{"{{estimates_table}}"}</code> emits a styled HTML table.
            Don't escape it; it must be placed as raw HTML, which the template body already is.
          </div>
        </div>
      </div>
    </div>
  );
}
